import { Box, Button, IconButton, Link, Menu, MenuItem, Skeleton, Typography } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import GoogleMap from 'google-maps-react-markers'
import React from 'react'
import map_icon from 'src/Libs/flow/images/google-map.jpg'
import { Marker } from './Marker'
import { getMapOptions } from './map-options'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { getCurrentLanguage } from 'src/Libs/languages/functions/default'
import { SimpleSettingsIcon } from 'src/Libs/flow/icons'

export interface MapCoordinates {
  lat: number
  lng: number
}

const DEFAULT_ZOOM = 15

const DEFAULT_LAT = 52.40133
const DEFAULT_LNG = 7.99817 //e3dc Karlstraße
const CookiesLink = 'https://www.google.com/intl/de_US/help/terms_maps/'

interface DisabledMapOptions {
  text: string
  buttonLabel: string
  loading: boolean
  onClick: () => void
  disagree: () => void
}

interface MapProps {
  coordinates: MapCoordinates | undefined
  disabled: boolean
  disabledOptions: DisabledMapOptions
  onGoogleReject?: () => void
}

export const FlowMap: React.FC<MapProps> = ({ coordinates, disabled = false, disabledOptions, onGoogleReject }) => {
  const { t } = useCustomTranslation()
  const language = getCurrentLanguage()

  const theme = useTheme()

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null)

  const centerPoint = {
    lat: coordinates?.lat ?? DEFAULT_LAT,
    lng: coordinates?.lng ?? DEFAULT_LNG,
  }

  const open = Boolean(anchorEl)

  const handleMapSettingsClick = (event: React.MouseEvent<HTMLElement>): void => {
    setAnchorEl(event.currentTarget)
  }

  const handleMapSettingsClose = (): void => {
    setAnchorEl(null)
  }

  if (disabled) return <DisabledMap disabledOptions={disabledOptions} />

  return (
    <Box width={'100%'} height={400}>
      <GoogleMap
        apiKey={process.env.REACT_APP_MAPS_TOKEN}
        defaultCenter={centerPoint}
        defaultZoom={DEFAULT_ZOOM}
        loadingContent={<Skeleton variant="rectangular" height={400} />}
        mapMinHeight="400px"
        externalApiParams={{ language }}
        options={getMapOptions(theme)}
      >
        {coordinates && <Marker lat={coordinates.lat} lng={coordinates.lng} />}
      </GoogleMap>

      <IconButton
        aria-controls={open ? 'account-menu' : undefined}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
        onClick={handleMapSettingsClick}
        sx={{
          position: 'absolute',
          bottom: theme => theme.spacing(3),
          right: theme => theme.spacing(1),
        }}
      >
        <SimpleSettingsIcon />
      </IconButton>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleMapSettingsClose}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <Box py={2} px={3}>
          <Typography>{t('user.googleAgreement.settings')}</Typography>
          <MenuItem sx={{ p: 0, mt: 2 }}>
            <Button
              variant="contained"
              size="large"
              color="secondary"
              disabled={!!disabledOptions?.loading}
              onClick={() => {
                handleMapSettingsClose()
                onGoogleReject?.()
                disabledOptions?.disagree?.()
              }}
            >
              {t('user.googleAgreement.disagree')}
            </Button>
          </MenuItem>
        </Box>
      </Menu>
    </Box>
  )
}

const DisabledMap: React.FC<{ disabledOptions: DisabledMapOptions }> = ({ disabledOptions }) => {
  const { t } = useCustomTranslation()

  return (
    <Box
      sx={{
        border: theme => `1px solid ${theme.palette.grey[600]}`,
        height: '100%',
        padding: 4,
      }}
    >
      {/* Disabled map image */}
      <Box
        sx={{
          backgroundImage: `url(${map_icon})`,
          filter: 'blur(2px)',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          backgroundSize: 'cover',
          position: 'absolute',
          top: 0,
          bottom: 0,
          left: 0,
          right: 0,
        }}
      />
      {/* Disabled map content */}
      <Box
        sx={{
          zIndex: 2,
          height: '100%',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          flexDirection: 'column',
          position: 'relative',
          backgroundColor: 'rgba(175, 175, 175, 0.6)',
          padding: 4,
        }}
      >
        <Typography fontWeight={'bold'}>{disabledOptions.text ?? t('common.noMapPossible')}</Typography>

        <Box pt={2}>
          <Link variant="body2" target="_blank" color="secondary" href={CookiesLink}>
            {t('user.googleAgreement.link')}
          </Link>
        </Box>

        <Button
          variant="contained"
          size="large"
          color="secondary"
          onClick={disabledOptions.onClick}
          disabled={disabledOptions.loading}
          sx={{ mt: 1.5 }}
        >
          {disabledOptions?.buttonLabel}
        </Button>
      </Box>
    </Box>
  )
}
